import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import "../../assets/css/Details.css";
import { FaEdit, FaTrashAlt, FaPlus, FaKey, FaArrowLeft, FaFacebookMessenger, FaCheck } from "react-icons/fa";
import { FaBoxesStacked } from "react-icons/fa6";
import { employeesAPI } from '../../api/apiClient';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import LocationAutocomplete from '../LocationAutocomplete';

export default function EmployeeDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // الحصول على رقم الصفحة من state التنقل إن وجد
  const fromPage = location.state?.fromPage || 1;
  const userId = location.state?.userId;
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState("");
  
  const [profileData, setProfileData] = useState({
    id: "",
    firstname: "",
    lastname: "",
    nicname: "",
    company_name: "",
    email: "",
    website: "",
    tax_no: "",
    court: "",
    responsible_person: "",
    street_address: "",
    addressone: "",
    city: "",
    phone: "",
    postal_code: "",
    is_confirmed: ""
  });

  const [editData, setEditData] = useState({
    firstname: "",
    lastname: "",
    nicname: "",
    email: "",
    addressone: "",
    street_address: "",
    postal_code: ""
  });

  const [passwordData, setPasswordData] = useState({
    password: '',
    password_confirmation: ''
  });

  // Fetch user details
  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await employeesAPI.show(id);
      
      if (response.success) {
        const userData = response.data.data;
        setProfileData(userData);
        setEditData(userData);
      } else {
        setError(response.message || t('failed_to_fetch_user'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_user'));
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  const handleLocationChange = (e) => {
    setEditData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
      ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
    }));
  };

  // Handle user update
  const handleUpdate = async () => {
    try {
      setLoading(true);
      
      // إنشاء كائن البيانات المرسلة بدون الحقول الفارغة
      const dataToSend = Object.fromEntries(
        Object.entries(editData).filter(([_, value]) => value !== null && value !== '')
      );
      
      const response = await employeesAPI.update(id, dataToSend);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('user_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowEditModal(false);
        fetchUserDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_update_user'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t('failed_to_update_user'),
        timer: 3000,
        showConfirmButton: false,
      });
      console.error('Error updating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmed = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_user'),
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: 'rgba(51, 221, 57, 1)',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('confirm'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await employeesAPI.confirm(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('confirmed'),
            text: t('users_confirmed_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          setProfileData(editData);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_confirm_user'),
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateOrder = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_user'),
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: 'rgba(51, 221, 57, 1)',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('confirm'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await employeesAPI.toggleCreateOrders(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('confirmed'),
            text: t('users_confirmed_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          setProfileData(editData);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_confirm_user'),
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    try {
      if (!passwordData.password || passwordData.password.length < 8) {
        setPasswordError(t("password_must_be_at_least_8_characters"));
        return;
      }

      if (passwordData.password !== passwordData.password_confirmation) {
        setPasswordError(t("passwords_do_not_match"));
        return;
      }

      setLoading(true);
      const response = await employeesAPI.update(id, {
        password: passwordData.password,
        password_confirmation: passwordData.password_confirmation
      });
      
      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({ password: '', password_confirmation: '' });
        setPasswordError("");
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t('password_updated_successfully'),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t('failed_to_update_password'),
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
          icon: "error",
          title: t("error"),
          text: t('failed_to_update_password'),
          timer: 3000,
          showConfirmButton: false,
        });
      console.error('Error updating password:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  // زر العودة إلى قائمة العملاء مع الحفاظ على رقم الصفحة
  const handleBack = () => {
    navigate('/admin/employees', { 
      state: { 
        fromPage: fromPage,
        scrollToUser: userId 
      } 
    });
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container-wrapper">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Header مع زر العودة */}
        <div className="custom-header text-white p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div className="d-flex align-items-center">
            <Button 
              variant="light" 
              className="me-3"
              onClick={handleBack}
              title={t('back_to_list')}
            >
              <FaArrowLeft />
            </Button>
            <h2 className="mb-0">{profileData.firstname} {profileData.lastname}</h2>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {profileData.is_confirmed === 0 &&
              <Button 
                variant="info" 
                onClick={handleConfirmed}
                disabled={loading}
                size="sm"
              >
                <FaCheck className="me-1" /> {t("confirm_user")}
              </Button>
            }
            {profileData.can_create_orders === 0 &&
              <Button 
                variant="info" 
                onClick={handleCreateOrder}
                disabled={loading}
                size="sm"
              >
                <FaCheck className="me-1" /> {t("can_create_orders")}
              </Button>
            }

            <button
              className="btn btn-info text-white"
              onClick={() => navigate(`/admin/employee-bookings/${profileData.id}`)}
            >
              <FaBoxesStacked className="me-1" />
              {t('bookings')}
            </button>
            <button
              className="btn btn-info text-white"
              onClick={() => navigate(`/admin/employee-transports/${profileData.id}`)}
            >
              <FaBoxesStacked className="me-1" />
              {t('transports')}
            </button>

            <button
              className="btn btn-light"
              onClick={() => setShowPasswordModal(true)}
            >
              <FaKey className="me-1" />
              {t('change_password')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowEditModal(true)}
            >
              <FaEdit className="me-1" />
              {t('edit_profile')}
            </button>
            <button
              className="btn btn-success"
              onClick={() => navigate(`/admin/client-list/chat-client/${profileData.id}`)}
            >
              <FaFacebookMessenger className="me-1" />
            </button>
          </div>
        </div>

        {/* Info Cards */}
        <div className="row mt-4">
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1">
                <strong>{profileData.firstname} {profileData.lastname}</strong>
              </p>
              <p className="mb-1"><b>{t('company_name')}: </b>{profileData.company_name}</p>
              <p className="mb-1"><b>{t('nachname')}: </b>{profileData.nicname}</p>
              <p className="mb-1"><b>{t('Email')}:</b> {profileData.email}</p>
              <p className="mb-1"><b>{t('phone')}:</b> {profileData.telphone}</p>
              <p className="mb-1"><b>{t('website')}:</b> <a href={profileData.website} target="_blank" rel="noopener noreferrer">{profileData.website}</a></p>
              <p className="mb-1"><b>{t('VAT/Tax_No')}:</b> {profileData.tax_no}</p>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card p-3 h-100">
              <p className="mb-1"><b>{t('local_court')}:</b> {profileData.court}</p>
              <p className="mb-1">
                <b>{t('responsible_person')}</b> {profileData.responsible_person}
              </p>
              <p className="mb-1"><b>{t('street_address')}:</b> {profileData.street_address}</p>
              <p className="mb-1"><b>{t('addressone')}:</b> {profileData.addressone}</p>
              <p className="mb-1"><b>{t('postal_code')}:</b> {profileData.postal_code}</p>
            </div>
          </div>
        </div>

        {/* Password Modal */}
        <Modal
          show={showPasswordModal}
          onHide={() => {
            setShowPasswordModal(false);
            setPasswordError("");
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('change_password')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {passwordError && <Alert variant="danger">{passwordError}</Alert>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>{t('new_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password: e.target.value});
                    setPasswordError("");
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>{t('confirm_password')}</Form.Label>
                <Form.Control
                  type="password"
                  value={passwordData.password_confirmation}
                  onChange={(e) => {
                    setPasswordData({...passwordData, password_confirmation: e.target.value});
                    setPasswordError("");
                  }}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError("");
              }}
              disabled={loading}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handlePasswordChange}
              disabled={loading}
            >
              {loading ? t('Save...') : t('save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Profile Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{t('edit_user')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('first_name')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={editData.firstname || ''}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('last_name')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={editData.lastname || ''}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('nicname')}</Form.Label>
              <Form.Control
                type="text"
                name="nicname"
                value={editData.nicname || ''}
                onChange={handleChange}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('email')} *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editData.email || ''}
                onChange={handleChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('street_address')}</Form.Label>
              <Form.Control
                type="text"
                name="street_address"
                value={editData.street_address || ''}
                onChange={handleChange}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('address_line_2')}</Form.Label>
              <Form.Control
                type="text"
                name="addressone"
                value={editData.addressone || ''}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPostalCode">
              <Form.Label>{t('postal_code')}</Form.Label>
              <LocationAutocomplete
                name="postal_code"
                value={editData.postal_code}
                onChange={handleLocationChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button variant="primary" onClick={handleUpdate} disabled={loading}>
              {loading ? t('updating') : t('save')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}